import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';

export interface AiPrompt {
  id: number;
  prompt_name: string;
  prompt_version: string;
  description: string | null;
  is_active: boolean;
  is_default: boolean;
  track_type: string;
  track_condition: string;
  day_of_week: string;
  track_name: string;
  form_weighting: number;
  market_weighting: number;
  system_prompt: string;
  analysis_instructions: string | null;
  market_analysis_instructions: string | null;
  track_specific_instructions: string | null;
  condition_specific_instructions: string | null;
  day_specific_instructions: string | null;
  output_format_instructions: string | null;
  priority: number;
  include_historical_strike_rate: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export type AiPromptDraft = Partial<AiPrompt>;

export interface AiPromptStats {
  total_prompts: number;
  active_prompts: number;
  default_prompts: number;
  track_types: number;
}

export interface AiPromptFilters {
  is_active?: 'true' | 'false';
  track_type?: string;
  track_condition?: string;
  day_of_week?: string;
}

function toQuery(filters: AiPromptFilters): string {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => {
    if (v) params.set(k, v);
  });
  const s = params.toString();
  return s ? `?${s}` : '';
}

export function useAiPrompts(filters: AiPromptFilters = {}) {
  return useQuery({
    queryKey: ['ai-prompts', filters],
    queryFn: () => apiClient<AiPrompt[]>(`/ai-prompts${toQuery(filters)}`),
  });
}

export function useAiPromptStats() {
  return useQuery({
    queryKey: ['ai-prompts', 'stats'],
    queryFn: () => apiClient<AiPromptStats>('/ai-prompts/stats'),
  });
}

function useInvalidate() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['ai-prompts'] });
}

export function useCreateAiPrompt() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (draft: AiPromptDraft) => apiClient<AiPrompt>('/ai-prompts', { method: 'POST', body: JSON.stringify(draft) }),
    onSuccess: invalidate,
  });
}

export function useUpdateAiPrompt() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, draft }: { id: number; draft: AiPromptDraft }) =>
      apiClient<AiPrompt>(`/ai-prompts/${id}`, { method: 'PUT', body: JSON.stringify(draft) }),
    onSuccess: invalidate,
  });
}

export function useDeleteAiPrompt() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: number) => apiClient(`/ai-prompts/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

export function useToggleAiPrompt() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: number) => apiClient<AiPrompt>(`/ai-prompts/${id}/toggle`, { method: 'POST' }),
    onSuccess: invalidate,
  });
}

export function useSetDefaultAiPrompt() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: number) => apiClient<AiPrompt>(`/ai-prompts/${id}/set-default`, { method: 'POST' }),
    onSuccess: invalidate,
  });
}

export function useDuplicateAiPrompt() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, new_name, new_version }: { id: number; new_name: string; new_version?: string }) =>
      apiClient<AiPrompt>(`/ai-prompts/${id}/duplicate`, { method: 'POST', body: JSON.stringify({ new_name, new_version }) }),
    onSuccess: invalidate,
  });
}
